import { useState, useEffect, useRef } from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { HoverCard, HoverCardContent, HoverCardTrigger } from '@/components/ui/hover-card';
import {
  Palette,
  Type,
  Layout,
  AlertTriangle,
  CheckCircle2,
  RefreshCw,
  Loader2,
  ChevronRight,
  ChevronDown,
  HelpCircle,
  Check,
  Lock,
  Unlock,
} from 'lucide-react';
import { ColorPicker } from '@/components/ui/color-picker';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { FontSearchPicker } from '@/components/ui/font-search-picker';
import StyleExplorer from './StyleExplorer';
import DesignSystemPreview from './DesignSystemPreview';
import { GOOGLE_FONTS, FONT_CATEGORIES } from '@/lib/fonts';

// ─── Style families available in the design system ───
const STYLE_FAMILIES = [
  { id: 'minimalism', label: 'Minimalism', desc: 'Clean, whitespace-driven, restrained',
    details: 'Centered grid with generous whitespace. No borders or shadows — pure typography and spacing create hierarchy. Every element earns its place.',
    effects: 'Fade-in on scroll, 200ms ease transitions on hover. No decorative elements — content is the design.' },
  { id: 'neumorphism', label: 'Neumorphism', desc: 'Soft shadows, embossed surfaces',
    details: 'Centered card-based layout with dual-direction soft shadows (inset and outset). Elements feel physically raised or pressed into the surface.',
    effects: 'Gentle press effects — scale(0.98) on click, smooth shadow transitions between raised and pressed states.' },
  { id: 'glassmorphism', label: 'Glassmorphism', desc: 'Frosted glass, translucent layers',
    details: 'Layered depth with frosted glass cards floating over vibrant gradient or image backgrounds. Translucent borders and backdrop blur create dimension.',
    effects: 'Backdrop blur (10-20px), subtle border (1px solid rgba white 0.2), light reflection, Z-depth layering.' },
  { id: 'brutalism', label: 'Brutalism', desc: 'Raw, confrontational, intentionally rough',
    details: 'Asymmetric, intentionally uncomfortable, grid-breaking layouts. Thick visible borders, hard offset shadows, no blur. Pure primary colors only.',
    effects: 'No smooth transitions — state changes are abrupt and intentional. Jarring section transitions. Anti-design aesthetic.' },
  { id: 'neubrutalism', label: 'Neubrutalism', desc: 'Playful bold borders, hard shadows',
    details: 'Asymmetric playful grid with offset elements. Thick colored borders (2-3px), hard colored offset shadows matching the accent color.',
    effects: 'Bouncy hover effects, playful scale transforms, staggered entrances. Kinetic and energetic feel throughout.' },
  { id: 'editorial', label: 'Editorial', desc: 'Magazine-style, asymmetric, serif',
    details: 'Magazine spread feel — mix narrow and wide columns, pull quotes, art-directed imagery. No two sections should feel the same.',
    effects: 'Text fades in on scroll, images reveal with a slight clip-path animation. Slow, deliberate pacing.' },
  { id: 'luxury', label: 'Luxury', desc: 'Ultra-refined, generous spacing, elegant',
    details: 'Ultra-generous spacing with deliberate pacing. Thin hairline rules, barely-there shadows. Each section is a moment of its own.',
    effects: 'Slow graceful entrance — fade-in over 600ms, parallax on scroll, elegant reveals. Small caps with wide letter-spacing.' },
  { id: 'dark mode', label: 'Dark Mode', desc: 'Dark surfaces, glow accents, depth',
    details: 'Cards and surfaces float on dark background (zinc-900/950). Subtle color-tinted glow shadows. Primary color for accents and CTAs.',
    effects: 'Subtle glow transitions, smooth hover brightness changes, fade-in reveals. Color-tinted shadows on interactive elements.' },
  { id: 'soft ui', label: 'Soft UI', desc: 'Gentle shadows, rounded, pastel',
    details: 'Rounded card containers with soft dual shadows creating depth without hard edges. Pastel palette with warm backgrounds.',
    effects: 'Gentle 250ms transitions, subtle hover lifts, no jarring movement. Everything feels cushioned and approachable.' },
  { id: 'bento', label: 'Bento Grid', desc: 'Mixed-size mosaic card layout',
    details: 'Grid-based mosaic with mixed-size cards. Large feature cards alongside small detail cards create visual rhythm through varying aspect ratios.',
    effects: 'Staggered entrance — cards appear one by one with slight delay, smooth hover lift effect on each card.' },
  { id: 'vibrant', label: 'Vibrant', desc: 'Bold colors, high energy, chunky',
    details: 'Bold alternating color blocks — each section has its own background color. High saturation everywhere. Chunky solid buttons.',
    effects: 'Bouncy scale transforms, staggered reveals, animated gradients. High-energy feel with playful interactions.' },
  { id: 'motion', label: 'Motion', desc: 'Scroll-driven transforms, parallax',
    details: 'Scroll-driven reveals where content appears and transforms as the user scrolls. Sections flow into each other with animated transitions.',
    effects: 'Parallax layers, scroll-triggered transforms, content reveals on intersection. Nav compacts on scroll.' },
  { id: 'retro', label: 'Retro', desc: 'Pixel borders, nostalgic, chunky',
    details: 'Asymmetric retro grid with visible structure. Thick pixel-style borders, hard offset shadows (no blur), vintage color combinations.',
    effects: 'Step-based animations, pixel transitions, retro cursor effects. Nostalgic visual rhythm.' },
  { id: 'organic', label: 'Organic', desc: 'Flowing shapes, earth tones, natural',
    details: 'Flowing natural layout with organic shapes — no rigid grid. Blob shapes as dividers, gentle curves throughout. Earth tones and warm colors.',
    effects: 'Subtle floating animation, organic movement on hover, nature-inspired transitions. Everything feels alive and breathing.' },
];


// ─── Editable Color Swatch (compact for left rail) ───
const EditableColorSwatch = ({ color, label, onChange }) => (
  <div className="flex items-center gap-2 p-2 bg-[var(--surface-1)] rounded-lg border border-[var(--border-default)]">
    <label className="relative cursor-pointer shrink-0">
      <div
        className="w-6 h-6 rounded border border-[var(--orbis-s2)]"
        style={{ backgroundColor: color }}
      />
      <input
        type="color"
        value={color}
        onChange={(e) => onChange(e.target.value)}
        className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
      />
    </label>
    <div className="flex-1 min-w-0">
      <span className="text-[10px] font-mono uppercase tracking-[0.08em] font-normal text-[var(--orbis-t3)] block leading-none">{label}</span>
      <input
        type="text"
        value={color}
        onChange={(e) => {
          const v = e.target.value;
          if (/^#[0-9a-fA-F]{0,6}$/.test(v)) onChange(v);
        }}
        className="block text-[11px] text-[var(--orbis-t2)] font-mono bg-transparent border-none outline-none w-full p-0 mt-0.5"
      />
    </div>
  </div>
);

// ─── Lock Toggle ───
const LockToggleWithTooltip = ({ checked, onChange, testId }) => (
  <div className="flex items-center gap-2">
    <span className="text-[11px] font-mono uppercase tracking-[0.1em] font-normal text-[var(--orbis-t3)]">Lock</span>
    <HoverCard>
      <HoverCardTrigger asChild>
        <button className="text-[var(--orbis-t4)] hover:text-[var(--orbis-t2)] transition-colors">
          <HelpCircle className="w-3.5 h-3.5" />
        </button>
      </HoverCardTrigger>
      <HoverCardContent className="w-64 bg-[var(--surface-2)] border border-[var(--border-default)] p-3">
        <div className="space-y-2">
          <p className="text-sm text-[var(--orbis-t1)] font-medium">Lock individual fields</p>
          <p className="text-xs text-[var(--orbis-t2)] leading-relaxed">
            When locked, this field won't change when you click Regenerate. Unlocked fields will cycle through design variants.
          </p>
        </div>
      </HoverCardContent>
    </HoverCard>
    <Switch
      checked={checked}
      onCheckedChange={onChange}
      data-testid={testId}
    />
  </div>
);

// ─── Collapsible Accordion Section (right spec panel) ───
const SpecSection = ({ title, count, defaultOpen = false, children }) => {
  const [open, setOpen] = useState(defaultOpen);

  return (
    <div className="border-b border-[var(--border-default)]">
      <button
        onClick={() => setOpen(prev => !prev)}
        className="w-full flex items-center justify-between py-3 px-1 group"
      >
        <span className="text-[11px] font-mono uppercase tracking-[0.1em] font-normal text-[var(--orbis-t3)]">
          {title}{count != null ? ` (${count})` : ''}
        </span>
        <ChevronDown className={`w-3 h-3 text-[var(--orbis-t2)] transition-transform duration-200 ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && (
        <div className="pb-4 px-1">
          {children}
        </div>
      )}
    </div>
  );
};

// ─── Helper: load Google Font dynamically ───
function useGoogleFont(fontName) {
  useEffect(() => {
    if (!fontName) return;
    const id = `gfont-${fontName.replace(/\s+/g, '-').toLowerCase()}`;
    if (document.getElementById(id)) return; // already loaded
    const link = document.createElement('link');
    link.id = id;
    link.rel = 'stylesheet';
    link.href = `https://fonts.googleapis.com/css2?family=${encodeURIComponent(fontName)}:wght@300;400;500;600;700&display=swap`;
    document.head.appendChild(link);
  }, [fontName]);
}

// ─── Refine chip options ───
const REFINE_CHIPS = [
  'Warmer colors',
  'More minimal',
  'Add glassmorphism',
  'Bolder typography',
  'Dark mode',
  'More editorial',
];

// ─── Main Component ───
export default function DesignSystemStage({ designSystem, magicState, onRegenerate, onApprove, isProcessing, designDiff }) {
  const [refinementText, setRefinementText] = useState('');
  const [styleExplorerOpen, setStyleExplorerOpen] = useState(false);
  const [isRegenerating, setIsRegenerating] = useState(false);
  const [isApproving, setIsApproving] = useState(false);
  const [changesOpen, setChangesOpen] = useState(false);
  const [appliedChips, setAppliedChips] = useState(new Set());
  const prefersReducedMotion = useReducedMotion();
  const fade = (axis, dist, delay) => prefersReducedMotion ? {} : {
    initial: { opacity: 0, [axis]: dist },
    animate: { opacity: 1, [axis]: 0 },
    transition: { delay, duration: 0.35, ease: [0.22, 1, 0.36, 1] },
  };
  const stagger = (i, baseDelay = 0.1) => prefersReducedMotion ? {} : {
    initial: { opacity: 0, y: 14 },
    animate: { opacity: 1, y: 0 },
    transition: { delay: baseDelay + i * 0.05, duration: 0.3, ease: [0.22, 1, 0.36, 1] },
  };

  // Restore lock state from backend if available (persists across navigation)
  const savedLocks = magicState?.design_system_locked || {};
  const [locked, setLocked] = useState({
    palette: savedLocks.palette || false,
    typography: savedLocks.typography || false,
    style: savedLocks.style || false,
  });

  // Local editable state — initialized from designSystem, user can override
  const [localColors, setLocalColors] = useState({});
  const [localHeadingFont, setLocalHeadingFont] = useState('');
  const [localBodyFont, setLocalBodyFont] = useState('');
  const [localStyleId, setLocalStyleId] = useState('');
  const userSelectedStyleRef = useRef(false); // tracks if user explicitly picked a style
  const [styleDropdownOpen, setStyleDropdownOpen] = useState(false);

  // Load selected Google Fonts dynamically so preview renders correctly
  useGoogleFont(localHeadingFont);
  useGoogleFont(localBodyFont);

  // Build merged font list — include backend fonts if not in the curated list
  const allFonts = (() => {
    const set = new Set(GOOGLE_FONTS);
    if (designSystem?.typography?.heading) set.add(designSystem.typography.heading);
    if (designSystem?.typography?.body) set.add(designSystem.typography.body);
    return [...set].sort();
  })();

  // Initialize local state from designSystem — keyed on a stable fingerprint
  // Normalizes hex case to prevent spurious resets
  const dsFingerprint = designSystem ? `${(designSystem.colors?.primary||'').toLowerCase()}-${designSystem.typography?.heading}-${(designSystem.style?.name||'').toLowerCase()}` : '';
  useEffect(() => {
    if (designSystem) {
      setLocalColors({
        primary: designSystem.colors?.primary || '#2563EB',
        secondary: designSystem.colors?.secondary || '#3B82F6',
        accent: designSystem.colors?.accent || '#F97316',
        background: designSystem.colors?.background || '#FFFFFF',
        text: designSystem.colors?.text || '#1E293B',
      });
      setLocalHeadingFont(designSystem.typography?.heading || 'Inter');
      setLocalBodyFont(designSystem.typography?.body || 'Inter');
      // Only reset style if user didn't explicitly pick one
      if (!userSelectedStyleRef.current) {
        const styleName = (designSystem.style?.name || '').toLowerCase();
        const matched = STYLE_FAMILIES.find(s => styleName.includes(s.id));
        setLocalStyleId(matched?.id || '');
      }
      userSelectedStyleRef.current = false;
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dsFingerprint]);

  if (!designSystem) {
    return (
      <div className="flex items-center justify-center h-[60vh]">
        <div className="text-center">
          <Loader2 className="w-8 h-8 text-[var(--orbis-t4)] animate-spin mx-auto mb-4" />
          <p className="text-[var(--orbis-t2)]">Generating design system...</p>
        </div>
      </div>
    );
  }

  const { style, anti_patterns, accessibility_checklist, key_effects, premium_mode,
    pattern, visual_spec, ux_guidelines, stack_guidelines, product_enrichment } = designSystem;

  const handleRegenerate = async (extra = {}) => {
    setIsRegenerating(true);
    try {
      await onRegenerate({
        locked,
        overrides: { colors: localColors, headingFont: localHeadingFont, bodyFont: localBodyFont, styleId: localStyleId },
        refinement_text: extra.refinement_text || null,
        selected_style: extra.selected_style || (locked.style && localStyleId ? localStyleId : null),
      });
    } finally {
      setIsRegenerating(false);
    }
  };

  const handleApprove = async () => {
    setIsApproving(true);
    try {
      await onApprove({ colors: localColors, headingFont: localHeadingFont, bodyFont: localBodyFont });
    } finally {
      setIsApproving(false);
    }
  };

  const updateColor = (key, value) => {
    setLocalColors(prev => ({ ...prev, [key]: value }));
  };

  const currentStyle = STYLE_FAMILIES.find(s =>
    s.id === localStyleId ||
    s.id === localStyleId?.toLowerCase() ||
    localStyleId?.toLowerCase()?.includes(s.id)
  );

  const handleChipClick = (chip) => {
    // Immediately trigger regeneration with this chip as refinement
    setAppliedChips(prev => new Set([...prev, chip]));
    handleRegenerate({ refinement_text: chip.toLowerCase() });
  };

  const hasChanges = designDiff && designDiff.some(d => d.changed);

  return (
    <div className="flex flex-col w-full" style={{ height: 'calc(100vh - 64px)', background: 'var(--surface-0)' }}>

      {/* ── 3-Zone Dashboard ── */}
      <div className="flex flex-1 min-h-0">

        {/* ═══════════════ LEFT RAIL (compact, fixed width) ═══════════════ */}
        <motion.div className="w-[480px] shrink-0 flex flex-col overflow-hidden" style={{ borderRight: '1px solid #27272A60' }} {...fade('x', -20, 0)}>
          <div className="flex flex-col gap-3 flex-1 min-h-0" style={{ padding: 20 }}>

            {/* ── Style — always expanded grid ── */}
            <div className="flex flex-col flex-1 min-h-0">
              <div className="flex items-center justify-between mb-2 shrink-0">
                <span className="text-[11px] font-mono uppercase tracking-[0.1em] font-normal text-[var(--orbis-t3)]">Style</span>
                <button
                  onClick={() => handleRegenerate({ selected_style: null })}
                  disabled={isRegenerating}
                  className="flex items-center gap-1 rounded-md transition-colors hover:bg-[var(--orbis-s2)]"
                  style={{ padding: '3px 8px', fontSize: 10, color: 'var(--orbis-t4)', fontFamily: 'var(--orbis-font)' }}
                >
                  <RefreshCw className="w-3 h-3" />
                  Shuffle
                </button>
              </div>
              <StyleExplorer
                selectedStyle={localStyleId || style?.name}
                onSelect={(styleName) => {
                  userSelectedStyleRef.current = true;
                  setLocalStyleId(styleName);
                  handleRegenerate({ selected_style: styleName });
                }}
              />
            </div>

            {/* ── Refine (compact pills + custom input) ── */}
            <div>
              <div className="flex items-center gap-1.5 mb-2">
                <span className="text-[11px] font-mono uppercase tracking-[0.1em] font-normal text-[var(--orbis-t3)]">Refine</span>
                <HoverCard>
                  <HoverCardTrigger asChild>
                    <button className="text-[rgba(240,239,232,0.2)] hover:text-[var(--orbis-t3)] transition-colors"><HelpCircle className="w-3 h-3" /></button>
                  </HoverCardTrigger>
                  <HoverCardContent className="w-56 bg-[var(--surface-2)] border border-[var(--border-default)] p-3">
                    <p className="text-xs text-[var(--orbis-t2)] leading-relaxed">Quick adjustments. Each chip instantly regenerates your design system with that change applied.</p>
                  </HoverCardContent>
                </HoverCard>
              </div>
              <div className="flex flex-wrap gap-1.5 mb-2">
                {REFINE_CHIPS.map(chip => {
                  const isApplied = appliedChips.has(chip);
                  return (
                    <button
                      key={chip}
                      onClick={() => handleChipClick(chip)}
                      className="flex items-center gap-1 px-2.5 py-1 rounded-full text-[12px] transition-colors"
                      style={{
                        background: isApplied ? 'rgba(184,204,223,0.1)' : 'var(--surface-1)',
                        border: isApplied ? '0.5px solid rgba(184,204,223,0.35)' : '0.5px solid var(--orbis-s2)',
                        color: isApplied ? 'var(--orbis-blue)' : 'var(--orbis-t2)',
                      }}
                    >
                      {isApplied && <Check className="w-2.5 h-2.5" />}
                      {chip}
                    </button>
                  );
                })}
              </div>
              {/* Custom refinement inline */}
              <div className="flex gap-1.5">
                <input
                  type="text"
                  value={refinementText}
                  onChange={e => setRefinementText(e.target.value)}
                  placeholder="e.g. More contrast, warmer tones..."
                  onKeyDown={e => {
                    if (e.key === 'Enter' && refinementText.trim()) {
                      handleRegenerate({ refinement_text: refinementText.trim() });
                      setRefinementText('');
                      setAppliedChips(new Set());
                    }
                  }}
                  className="flex-1 h-8 px-3 text-[12px] bg-[var(--surface-1)] border border-[var(--border-default)] rounded-lg text-[var(--orbis-t1)] placeholder-[rgba(240,239,232,0.2)] outline-none focus:border-[var(--border-hover)]"
                />
                <button
                  onClick={() => {
                    if (refinementText.trim()) {
                      handleRegenerate({ refinement_text: refinementText.trim() });
                      setRefinementText('');
                      setAppliedChips(new Set());
                    }
                  }}
                  disabled={!refinementText.trim() || isRegenerating}
                  className="h-8 px-3 rounded-lg text-[12px] bg-[var(--surface-1)] border border-[var(--border-default)] text-[var(--orbis-t2)] hover:border-[var(--border-hover)] disabled:opacity-30 disabled:cursor-not-allowed transition-colors shrink-0"
                >
                  {isRegenerating ? <Loader2 className="w-3 h-3 animate-spin" /> : '\u2192'}
                </button>
              </div>
            </div>

            {/* ── Changes (only when there are changes) ── */}
            {hasChanges && (
              <div className="mt-auto pt-2 border-t border-[var(--border-default)]">
                <button
                  onClick={() => setChangesOpen(prev => !prev)}
                  className="flex items-center gap-2 w-full text-left py-1"
                >
                  <span className="text-[11px] font-mono uppercase tracking-[0.1em] font-normal text-[var(--orbis-t3)]">
                    Changes ({designDiff.filter(d => d.changed).length})
                  </span>
                  <ChevronDown className={`w-3 h-3 text-[var(--orbis-t2)] transition-transform duration-200 ${changesOpen ? 'rotate-180' : ''}`} />
                </button>
                {changesOpen && (
                  <div className="mt-1 space-y-1">
                    {designDiff.filter(d => d.changed).map(d => (
                      <div key={d.category} className="px-2 py-1 rounded text-[11px] font-mono text-[var(--orbis-t1)] bg-[var(--surface-1)]">
                        <span className="uppercase tracking-[0.1em] text-[rgba(240,239,232,0.4)]">{d.category}</span>
                        <span className="block mt-0.5 text-[var(--orbis-t2)]">{d.old} → {d.new}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        </motion.div>

        {/* ═══════════════ CENTER CANVAS (flex-1) ═══════════════ */}
        <div className="flex-1 min-w-0 flex flex-col overflow-y-auto">
          {/* Style details header — expanded info */}
          <motion.div className="shrink-0 bg-[var(--surface-0)]" style={{ padding: '16px 20px', borderBottom: '1px solid #27272A60' }} {...fade('y', -12, 0.05)}>
            <div className="flex items-start justify-between gap-4">
              <div className="flex-1 min-w-0">
                <h2 style={{ fontSize: 18, fontWeight: 500, color: 'var(--orbis-t1)', fontFamily: 'var(--orbis-font)' }}>
                  {localStyleId || style?.name || 'Design System'}
                </h2>
                {style?.keywords && (
                  <p style={{ fontSize: 12, color: 'var(--orbis-t4)', marginTop: 4, fontFamily: 'var(--orbis-font)' }}>{style.keywords}</p>
                )}
              </div>
              </div>
            {/* Style details — effects, visual spec */}
            {(currentStyle?.effects || visual_spec) && (
              <div className="flex flex-wrap gap-x-6 gap-y-1 mt-3">
                {visual_spec?.nav_style && (
                  <div className="flex items-center gap-1.5">
                    <span style={{ fontSize: 9, color: 'var(--orbis-s3)', fontFamily: 'var(--orbis-mono)', textTransform: 'uppercase', letterSpacing: '0.08em' }}>Nav</span>
                    <span style={{ fontSize: 11, color: 'var(--orbis-t3)', fontFamily: 'var(--orbis-font)' }}>{typeof visual_spec.nav_style === 'string' ? visual_spec.nav_style.substring(0, 40) : ''}</span>
                  </div>
                )}
                {visual_spec?.hero_pattern && (
                  <div className="flex items-center gap-1.5">
                    <span style={{ fontSize: 9, color: 'var(--orbis-s3)', fontFamily: 'var(--orbis-mono)', textTransform: 'uppercase', letterSpacing: '0.08em' }}>Hero</span>
                    <span style={{ fontSize: 11, color: 'var(--orbis-t3)', fontFamily: 'var(--orbis-font)' }}>{typeof visual_spec.hero_pattern === 'string' ? visual_spec.hero_pattern.substring(0, 40) : ''}</span>
                  </div>
                )}
                {visual_spec?.cta_style && (
                  <div className="flex items-center gap-1.5">
                    <span style={{ fontSize: 9, color: 'var(--orbis-s3)', fontFamily: 'var(--orbis-mono)', textTransform: 'uppercase', letterSpacing: '0.08em' }}>CTA</span>
                    <span style={{ fontSize: 11, color: 'var(--orbis-t3)', fontFamily: 'var(--orbis-font)' }}>{typeof visual_spec.cta_style === 'string' ? visual_spec.cta_style.substring(0, 40) : ''}</span>
                  </div>
                )}
                {visual_spec?.animation?.personality && (
                  <div className="flex items-center gap-1.5">
                    <span style={{ fontSize: 9, color: 'var(--orbis-s3)', fontFamily: 'var(--orbis-mono)', textTransform: 'uppercase', letterSpacing: '0.08em' }}>Motion</span>
                    <span style={{ fontSize: 11, color: 'var(--orbis-t3)', fontFamily: 'var(--orbis-font)' }}>{visual_spec.animation.personality}</span>
                  </div>
                )}
              </div>
            )}
          </motion.div>

          {/* Live Preview — Relume-inspired sections */}
          <div className="flex-1" style={{ padding: 20 }}>

            {/* ── Colors Section — editable swatches ── */}
            <motion.div className="mb-8" {...fade('y', 20, 0.1)}>
              <div className="flex items-center justify-between mb-4">
                <h3 style={{ fontSize: 16, fontWeight: 500, color: 'var(--orbis-t1)', fontFamily: 'var(--orbis-font)' }}>Colors</h3>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setLocked(prev => ({ ...prev, palette: !prev.palette }))}
                    className="flex items-center gap-1 rounded-md transition-colors hover:bg-[var(--orbis-s2)]"
                    style={{ padding: '3px 8px', fontSize: 10, color: locked.palette ? 'var(--orbis-blue)' : 'var(--orbis-s3)', fontFamily: 'var(--orbis-font)' }}
                    title={locked.palette ? 'Colors locked — won\'t change on regenerate' : 'Colors unlocked'}
                  >
                    {locked.palette ? <Lock className="w-3 h-3" /> : <Unlock className="w-3 h-3" />}
                  </button>
                  <button
                    onClick={() => handleRegenerate({ refinement_text: 'new color palette' })}
                    disabled={isRegenerating || locked.palette}
                    className="flex items-center gap-1.5 rounded-md transition-colors hover:bg-[var(--orbis-s2)] disabled:opacity-30"
                    style={{ padding: '4px 10px', fontSize: 11, color: 'var(--orbis-t4)', fontFamily: 'var(--orbis-font)' }}
                  >
                    <RefreshCw className="w-3 h-3" />
                    Shuffle
                  </button>
                </div>
              </div>
              {/* Large color swatch cards — click to open color picker */}
              <div className="grid grid-cols-5 gap-3">
                {[
                  { key: 'primary', label: 'Primary' },
                  { key: 'secondary', label: 'Secondary' },
                  { key: 'accent', label: 'Accent' },
                  { key: 'background', label: 'Background' },
                  { key: 'text', label: 'Text' },
                ].map(({ key, label }, i) => {
                  const color = localColors[key] || '#000000';
                  const isLight = color.toLowerCase() === '#ffffff' || color.toLowerCase() === '#fff' || color.toLowerCase().startsWith('#f');
                  return (
                    <motion.div key={key} {...stagger(i, 0.15)}>
                    <ColorPicker
                      value={color}
                      onValueChange={(hex) => updateColor(key, hex)}
                      mode="solid"
                    >
                      <button className="rounded-xl overflow-hidden cursor-pointer text-left w-full transition-all hover:ring-1 hover:ring-[var(--orbis-s3)]" style={{ border: '1px solid var(--orbis-s2)' }}>
                        <div style={{ height: 80, background: color, position: 'relative' }}>
                          <span style={{
                            position: 'absolute', bottom: 8, left: 10,
                            fontSize: 10, fontWeight: 500, fontFamily: 'var(--orbis-mono)',
                            color: isLight ? 'var(--orbis-bg)' : '#FFFFFF',
                            opacity: 0.7,
                          }}>
                            {color.toUpperCase()}
                          </span>
                        </div>
                        <div style={{ padding: '8px 10px', background: 'var(--orbis-s1)' }}>
                          <span style={{ fontSize: 11, color: 'var(--orbis-t2)', fontFamily: 'var(--orbis-font)' }}>{label}</span>
                        </div>
                      </button>
                    </ColorPicker>
                    </motion.div>
                  );
                })}
              </div>
            </motion.div>

            {/* ── Typography Section — editable font cards ── */}
            <motion.div className="mb-8" {...fade('y', 20, 0.25)}>
              <div className="flex items-center justify-between mb-4">
                <h3 style={{ fontSize: 16, fontWeight: 500, color: 'var(--orbis-t1)', fontFamily: 'var(--orbis-font)' }}>Typography</h3>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setLocked(prev => ({ ...prev, typography: !prev.typography }))}
                    className="flex items-center gap-1 rounded-md transition-colors hover:bg-[var(--orbis-s2)]"
                    style={{ padding: '3px 8px', fontSize: 10, color: locked.typography ? 'var(--orbis-blue)' : 'var(--orbis-s3)', fontFamily: 'var(--orbis-font)' }}
                    title={locked.typography ? 'Typography locked — won\'t change on regenerate' : 'Typography unlocked'}
                  >
                    {locked.typography ? <Lock className="w-3 h-3" /> : <Unlock className="w-3 h-3" />}
                  </button>
                  <button
                    onClick={() => handleRegenerate({ refinement_text: 'new typography pairing' })}
                    disabled={isRegenerating || locked.typography}
                    className="flex items-center gap-1.5 rounded-md transition-colors hover:bg-[var(--orbis-s2)] disabled:opacity-30"
                    style={{ padding: '4px 10px', fontSize: 11, color: 'var(--orbis-t4)', fontFamily: 'var(--orbis-font)' }}
                  >
                    <RefreshCw className="w-3 h-3" />
                    Shuffle
                  </button>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <motion.div className="rounded-xl p-5" style={{ border: '1px solid var(--orbis-s2)', background: 'var(--orbis-s1)' }} {...stagger(0, 0.3)}>
                  <div className="flex items-center justify-between mb-3">
                    <span style={{ fontSize: 11, color: 'var(--orbis-t3)', fontFamily: 'var(--orbis-font)' }}>Heading</span>
                  </div>
                  <p style={{ fontSize: 28, fontWeight: 600, color: 'var(--orbis-t1)', lineHeight: 1.2, fontFamily: `"${localHeadingFont}", sans-serif` }}>
                    {localHeadingFont}
                  </p>
                  <p className="mt-2" style={{ fontSize: 14, color: 'var(--orbis-t4)', fontFamily: `"${localHeadingFont}", sans-serif` }}>
                    Aa Bb Cc Dd Ee Ff Gg
                  </p>
                  {/* Font selector — search with categories */}
                  <FontSearchPicker
                    value={localHeadingFont}
                    onChange={setLocalHeadingFont}
                    className="mt-3"
                  />
                </motion.div>
                <motion.div className="rounded-xl p-5" style={{ border: '1px solid var(--orbis-s2)', background: 'var(--orbis-s1)' }} {...stagger(1, 0.3)}>
                  <div className="flex items-center justify-between mb-3">
                    <span style={{ fontSize: 11, color: 'var(--orbis-t3)', fontFamily: 'var(--orbis-font)' }}>Body</span>
                  </div>
                  <p style={{ fontSize: 24, fontWeight: 400, color: 'var(--orbis-t1)', lineHeight: 1.2, fontFamily: `"${localBodyFont}", sans-serif` }}>
                    {localBodyFont}
                  </p>
                  {designSystem.typography?.mood && (
                    <p className="mt-2" style={{ fontSize: 11, color: 'var(--orbis-t4)', fontFamily: 'var(--orbis-mono)' }}>
                      {designSystem.typography.mood}
                    </p>
                  )}
                  {/* Font selector — search with categories */}
                  <FontSearchPicker
                    value={localBodyFont}
                    onChange={setLocalBodyFont}
                    className="mt-3"
                  />
                </motion.div>
              </div>
            </motion.div>

            {/* ── UI Styling Section ── */}
            <motion.div {...fade('y', 20, 0.38)}>
              <div className="flex items-center justify-between mb-4">
                <h3 style={{ fontSize: 16, fontWeight: 500, color: 'var(--orbis-t1)', fontFamily: 'var(--orbis-font)' }}>UI Styling</h3>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setLocked(prev => ({ ...prev, style: !prev.style }))}
                    className="flex items-center gap-1 rounded-md transition-colors hover:bg-[var(--orbis-s2)]"
                    style={{ padding: '3px 8px', fontSize: 10, color: locked.style ? 'var(--orbis-blue)' : 'var(--orbis-s3)', fontFamily: 'var(--orbis-font)' }}
                    title={locked.style ? 'UI styling locked — won\'t change on regenerate' : 'UI styling unlocked'}
                  >
                    {locked.style ? <Lock className="w-3 h-3" /> : <Unlock className="w-3 h-3" />}
                  </button>
                </div>
              </div>
              <DesignSystemPreview
                colors={localColors}
                headingFont={localHeadingFont}
                bodyFont={localBodyFont}
                companyName={magicState?.brief?.company_name}
                borderRadius={visual_spec?.border_radius}
                styleName={localStyleId || style?.name}
                className="grid grid-cols-2 gap-3"
              />
            </motion.div>
          </div>
        </div>

        {/* ═══════════════ RIGHT SPEC PANEL (280px) ═══════════════ */}
        <motion.div className="w-[300px] shrink-0 overflow-y-auto" style={{ borderLeft: '1px solid #27272A60' }} {...fade('x', 20, 0.15)}>
          <div style={{ padding: 20 }}>
            {/* Panel title */}
            <div className="flex items-center gap-1.5 mb-3 pb-3 border-b border-[var(--border-default)]">
              <span className="text-[11px] font-mono uppercase tracking-[0.1em] font-normal text-[var(--orbis-t3)]">AI Build Guide</span>
              <HoverCard>
                <HoverCardTrigger asChild>
                  <button className="text-[rgba(240,239,232,0.2)] hover:text-[var(--orbis-t3)] transition-colors"><HelpCircle className="w-3 h-3" /></button>
                </HoverCardTrigger>
                <HoverCardContent className="w-60 bg-[var(--surface-2)] border border-[var(--border-default)] p-3">
                  <p className="text-xs text-[var(--orbis-t2)] leading-relaxed">These rules guide the AI when building your website. Expand any section to review the constraints the AI will follow during generation.</p>
                </HoverCardContent>
              </HoverCard>
            </div>

            {/* LANDING PATTERN */}
            {pattern?.name && (
              <SpecSection title="Landing Pattern" defaultOpen={false}>
                <p className="text-[14px] text-[var(--orbis-t2)] font-medium mb-3">{pattern.name}</p>
                {pattern.sections && (
                  <div className="space-y-1.5">
                    {pattern.sections.split(/[>,\u2192]+/).map((section, i, arr) => (
                      <div key={i} className="flex items-center gap-2">
                        <span className="w-4 text-[12px] font-mono text-[var(--orbis-t2)] text-right shrink-0">{i + 1}</span>
                        <span className="text-[13px] text-[var(--orbis-t2)]">{section.trim().replace(/^\d+\.\s*/, '')}</span>
                        {i < arr.length - 1 && (
                          <span className="text-[12px] text-[var(--orbis-t4)] ml-auto">\u2192</span>
                        )}
                      </div>
                    ))}
                  </div>
                )}
                {pattern.cta_placement && (
                  <p className="text-[12px] text-[var(--orbis-t2)] mt-3 font-mono">
                    <span className="uppercase tracking-wider">CTA</span> {pattern.cta_placement}
                  </p>
                )}
              </SpecSection>
            )}

            {/* VISUAL PERSONALITY */}
            {visual_spec && (
              <SpecSection title="Visual Personality" defaultOpen={false}>
                <div className="space-y-0">
                  {[
                    ['Animation', visual_spec.animation?.personality],
                    ['Nav Style', visual_spec.nav_style],
                    ['Hero Pattern', visual_spec.hero_pattern],
                    ['CTA Style', visual_spec.cta_style],
                    ['Spacing', visual_spec.section_rhythm ? visual_spec.section_rhythm.slice(0, 80) : null],
                  ].filter(([, v]) => v).map(([label, value]) => (
                    <div key={label} className="flex items-start gap-3 py-2 border-b border-[var(--orbis-s2)]">
                      <span className="text-[11px] font-mono uppercase tracking-[0.1em] font-normal text-[var(--orbis-t3)] w-20 shrink-0 pt-0.5">{label}</span>
                      <span className="text-[14px] text-[var(--orbis-t2)] leading-relaxed">{value}</span>
                    </div>
                  ))}
                </div>
              </SpecSection>
            )}

            {/* UX GUIDELINES */}
            {ux_guidelines?.length > 0 && (
              <SpecSection title="UX Guidelines" count={ux_guidelines.length} defaultOpen={false}>
                <div className="space-y-2">
                  {ux_guidelines.slice(0, 5).map((ux, i) => (
                    <div key={i} className="py-1.5">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-[13px] text-[var(--orbis-t2)]">{ux.Category}: {ux.Issue}</span>
                        <span className={`text-[11px] font-mono px-1.5 py-0.5 rounded ${
                          ux.Severity === 'High'
                            ? 'text-[#4ade80] bg-[rgba(74,222,128,0.1)]'
                            : ux.Severity === 'Medium'
                              ? 'text-[#818cf8] bg-[rgba(129,140,248,0.1)]'
                              : 'text-[var(--orbis-t2)] bg-[var(--surface-2)]'
                        }`}>{ux.Severity}</span>
                      </div>
                      <div className="flex gap-3">
                        {ux.Do && <p className="text-[12px] text-[var(--orbis-t2)]">Do: {ux.Do}</p>}
                        {ux["Don't"] && <p className="text-[12px] text-[var(--orbis-t2)]">Don't: {ux["Don't"]}</p>}
                      </div>
                    </div>
                  ))}
                </div>
              </SpecSection>
            )}

            {/* TAILWIND GUIDELINES */}
            {stack_guidelines?.length > 0 && (
              <SpecSection title="Tailwind Guidelines" count={stack_guidelines.length} defaultOpen={false}>
                <div className="space-y-2">
                  {stack_guidelines.slice(0, 3).map((sg, i) => (
                    <div key={i} className="flex items-start gap-2 py-1.5">
                      <span className={`text-[11px] font-mono px-1.5 py-0.5 rounded shrink-0 ${
                        sg.Severity === 'High'
                          ? 'text-[#4ade80] bg-[rgba(74,222,128,0.1)]'
                          : sg.Severity === 'Medium'
                            ? 'text-[#818cf8] bg-[rgba(129,140,248,0.1)]'
                            : 'text-[var(--orbis-t2)] bg-[var(--surface-2)]'
                      }`}>{sg.Severity}</span>
                      <span className="text-[13px] text-[var(--orbis-t2)]">
                        <span className="text-[var(--orbis-t1)]">{sg.Guideline}</span> -- {sg.Do}
                      </span>
                    </div>
                  ))}
                </div>
              </SpecSection>
            )}

            {/* NOTES (renamed from Anti-patterns) */}
            {anti_patterns && (
              <SpecSection title="Notes" defaultOpen={false}>
                <div className="space-y-1.5">
                  {anti_patterns.split('+').map((item, i) => (
                    <p key={i} className="text-[14px] text-[var(--orbis-t2)] flex items-start gap-2">
                      <span className="text-[var(--orbis-t2)] shrink-0">&bull;</span>
                      <span>{item.trim()}</span>
                    </p>
                  ))}
                </div>
              </SpecSection>
            )}

            {/* Accessibility */}
            {accessibility_checklist && (
              <SpecSection title="Accessibility" count={accessibility_checklist.length} defaultOpen={false}>
                <div className="space-y-1.5">
                  {accessibility_checklist.slice(0, 5).map((item, i) => (
                    <p key={i} className="text-[13px] text-[var(--orbis-t2)] flex items-start gap-2">
                      <span className="text-emerald-500 shrink-0">\u25CB</span>
                      <span>{item}</span>
                    </p>
                  ))}
                </div>
              </SpecSection>
            )}

          </div>
        </motion.div>
      </div>

      {/* ═══════════════ BOTTOM BAR ═══════════════ */}
      <div className="magic-bottom-bar">
        {/* Left: Regenerate */}
        <button
          onClick={() => handleRegenerate()}
          disabled={isRegenerating}
          className="flex items-center justify-center gap-2 transition-colors rounded-full"
          style={{
            height: '44px', padding: '0 24px',
            background: 'var(--orbis-s1)', border: '0.5px solid var(--orbis-s2)',
            fontSize: '12px', fontWeight: 400, color: 'var(--orbis-t3)',
            cursor: isRegenerating ? 'not-allowed' : 'pointer',
            opacity: isRegenerating ? 0.5 : 1,
            fontFamily: 'var(--orbis-font)',
          }}
          data-testid="regenerate-btn"
        >
          {isRegenerating ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : (
            <>
              <RefreshCw className="w-4 h-4" />
              Regenerate
            </>
          )}
        </button>

        {/* Center label */}
        <span style={{ fontSize: '11px', color: 'var(--orbis-s3)', fontFamily: 'var(--orbis-font)', letterSpacing: '0.5px' }}>
          Design System · Step 2 of 7
        </span>

        {/* Right: Approve with glow ring */}
        <div className="orbis-cta-ring rounded-full p-0.5">
          <button
            onClick={handleApprove}
            disabled={isApproving}
            className="orbis-cta flex items-center justify-center gap-2 transition-all rounded-full"
            style={{
              height: '44px', padding: '0 28px',
              opacity: isApproving ? 0.4 : 1,
              fontSize: '12px', fontWeight: 500,
              cursor: isApproving ? 'not-allowed' : 'pointer',
              fontFamily: 'var(--orbis-font)',
            }}
            data-testid="approve-btn"
          >
            {isApproving ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <>
                <Check className="w-4 h-4" />
                Approve & Continue
                <ChevronRight className="w-4 h-4" />
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
